import {BaseBadge, BaseCard, BasePagination, BaseTable} from "@/components";
import type {Column} from "@/components/BaseTable/BaseTable";
import {useTransactions, useTransactionsStats} from "@/hooks/useTransactions";
import {StatsCard} from "@/pages/Dashboard/Home/components/StatsCard";
import type {Transaction, TransactionStatus, TransactionType} from "@/types/transaction.types";
import {formatCurrencyRaw} from "@/utils/format";
import {ArrowLeftRight, CheckCircle, Clock, DollarSign} from "lucide-react";
import {useState} from "react";
import {TransactionFilters} from "./components/TransactionFilters";

function statusVariant(status: TransactionStatus): "success" | "danger" | "warning" | "info" | "neutral" {
  const map: Record<TransactionStatus, "success" | "danger" | "warning" | "info" | "neutral"> = {
    completed: "success",
    failed: "danger",
    refunded: "warning",
    cancelled: "neutral",
    pending: "warning",
    processing: "info",
  };
  return map[status];
}

function statusLabel(status: TransactionStatus): string {
  const map: Record<TransactionStatus, string> = {
    completed: "Completado",
    failed: "Fallido",
    refunded: "Reembolsado",
    cancelled: "Cancelado",
    pending: "Pendiente",
    processing: "Procesando",
  };
  return map[status];
}

function typeLabel(type: TransactionType): string {
  const map: Record<TransactionType, string> = {
    remittance: "Remesa",
    deposit: "Depósito",
    withdrawal: "Retiro",
    p2p_transfer: "P2P",
  };
  return map[type];
}

function formatDate(iso: string): string {
  return new Intl.DateTimeFormat("es-CL", {dateStyle: "short", timeStyle: "short"}).format(new Date(iso));
}

const columns: Column<Transaction>[] = [
  {
    key: "id",
    header: "ID",
    render: row => <span className="font-mono text-xs text-(--color-text-secondary)">{row.id}</span>,
  },
  {
    key: "type",
    header: "Tipo",
    render: row => {
      const typeColors: Record<TransactionType, string> = {
        remittance: "text-blue-600 bg-blue-50",
        deposit: "text-emerald-600 bg-emerald-50",
        withdrawal: "text-rose-600 bg-rose-50",
        p2p_transfer: "text-purple-600 bg-purple-50",
      };
      return (
        <span className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${typeColors[row.type]}`}>
          {typeLabel(row.type)}
        </span>
      );
    },
  },
  {
    key: "userEmail",
    header: "Usuario",
    render: row => <span className="text-sm text-(--color-text-primary)">{row.userEmail}</span>,
  },
  {
    key: "amount",
    header: "Monto",
    render: row => (
      <span className="text-sm font-semibold text-(--color-text-primary) tabular-nums">
        {formatCurrencyRaw(row.amount, row.currency)}
      </span>
    ),
  },
  {
    key: "corridor",
    header: "Corredor",
    render: row => <span className="text-sm text-(--color-text-secondary)">{row.corridor ?? "—"}</span>,
  },
  {
    key: "status",
    header: "Estado",
    render: row => (
      <BaseBadge
        variant={statusVariant(row.status)}
        label={statusLabel(row.status)}
        dot
      />
    ),
  },
  {
    key: "createdAt",
    header: "Fecha",
    render: row => (
      <span className="text-xs text-(--color-text-tertiary) whitespace-nowrap">{formatDate(row.createdAt)}</span>
    ),
  },
];

export function Transactions() {
  const [typeFilter, setTypeFilter] = useState<TransactionType | "all">("all");
  const [statusFilter, setStatusFilter] = useState<TransactionStatus | "all">("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [page, setPage] = useState(1);
  const [limit] = useState(20);

  const {data, isLoading} = useTransactions({
    page,
    limit,
    type: typeFilter,
    status: statusFilter,
    ...(searchQuery ? {search: searchQuery} : {}),
  });
  const {data: stats} = useTransactionsStats();

  const items = data?.items ?? [];
  const pagination = data?.pagination ?? {total: 0, page: 1, limit: 20, totalPages: 1};

  function handleReset() {
    setTypeFilter("all");
    setStatusFilter("all");
    setSearchQuery("");
    setPage(1);
  }

  function handleFilterChange<T>(setter: (v: T) => void) {
    return (v: T) => {
      setter(v);
      setPage(1);
    };
  }

  return (
    <div className="flex flex-col gap-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-(--color-text-primary)">Transacciones</h1>
          <p className="text-sm text-(--color-text-secondary) mt-1">{pagination.total} transacciones</p>
        </div>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
          <StatsCard
            label="Total"
            value={stats.totalTransactions.toLocaleString()}
            icon={<ArrowLeftRight size={20} />}
            iconColor="var(--color-primary)"
          />
          <StatsCard
            label="Volumen (USD)"
            value={`$${(stats.totalVolumeUsd / 100).toLocaleString()}`}
            icon={<DollarSign size={20} />}
            iconColor="var(--color-accent)"
          />
          <StatsCard
            label="Tasa Éxito"
            value={`${stats.successRate.toFixed(1)}%`}
            icon={<CheckCircle size={20} />}
            iconColor="var(--color-success)"
          />
          <StatsCard
            label="Pendientes"
            value={stats.pending.toLocaleString()}
            icon={<Clock size={20} />}
            iconColor="var(--color-warning)"
          />
        </div>
      )}

      {/* Filters */}
      <BaseCard padding="md">
        <TransactionFilters
          typeFilter={typeFilter}
          statusFilter={statusFilter}
          searchQuery={searchQuery}
          onTypeChange={handleFilterChange(setTypeFilter)}
          onStatusChange={handleFilterChange(setStatusFilter)}
          onSearchChange={handleFilterChange(setSearchQuery)}
          onReset={handleReset}
        />
      </BaseCard>

      {/* Table */}
      <BaseCard padding="none">
        <BaseTable<Transaction>
          data={items}
          columns={columns}
          loading={isLoading}
          emptyMessage="No se encontraron transacciones con los filtros seleccionados."
        />
        {pagination.totalPages > 1 && (
          <BasePagination
            page={page}
            totalPages={pagination.totalPages}
            total={pagination.total}
            limit={limit}
            onPageChange={setPage}
          />
        )}
      </BaseCard>
    </div>
  );
}
